import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useAccountPartCategories from "../../data/useAccountPartCategories";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IAccount } from "../../models/account/IAccount";
import { IAccountPartCategory } from "../../models/account/IAccountPartCategory";
import { IPartCategory } from "../../models/Part";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import PartCategoryCombo from "../leads/LeadParts/PartCategoryCombo";
import LoadingWheel from "../shared/LoadingWheel";
import Tooltip from "../shared/Tooltip";
import useNotification from "../notifications/useNotifications";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";
import PriceFormat from "../../utils/priceFormat";

interface AccountPartCategoriesProps {
  account: IAccount;
}

export default function AccountPartCategories({
  account,
}: AccountPartCategoriesProps) {
  const { t } = useTranslation("accounts");
  const { accountPartCategories, update } = useAccountPartCategories(account.id, "AccountPartCategories");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Account Part Categories"));
  const { addNotification } = useNotification();
  const hasPartProTrade = useFeatureEnabled("PartPro Trade");
  const [selectedCategories, setSelectedCategories] = useState<IAccountPartCategory[]>([]);

  const sortedSelectedCategories = useMemo(
    () =>
      selectedCategories
        .slice()
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [selectedCategories]
  );

  useEffect(() => {
    setSelectedCategories(accountPartCategories.data?.data ?? []);
  }, [accountPartCategories.data]);

  const isLoading = accountPartCategories.isLoading;
  const isError = accountPartCategories.isError;

  const handleAddCategory = (category: IPartCategory) => {
    if (!category?.id) return;

    setSelectedCategories((prev) => {
      if (prev.some((selectedCategory) => selectedCategory.id === category.id)) {
        return prev;
      }

      return [...prev, { id: category.id, name: category.name, autoSelect: false }];
    });
  };

  const handleRemoveCategory = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.filter((category) => category.id !== categoryId)
    );
  };

  const handleToggleAutoSelect = (categoryId: string, autoSelect: boolean) => {
    setSelectedCategories((prev) =>
      prev.map((category) => (category.id === categoryId ? { ...category, autoSelect } : category))
    );
  };

  const handleSave = () => {
    update.mutate(
      {
        partCategoryIds: selectedCategories.map((category) => category.id),
        autoSelectPartCategoryIds: selectedCategories
          .filter((category) => category.autoSelect)
          .map((category) => category.id),
      },
      {
        ...saveHandlers,
        onSuccess: (res) => {
          saveHandlers.onSuccess();
          // The Core save stands, but a linked PartPro Trade buyer wasn't updated
          if (res?.message?.includes("PartPro Trade was not updated")) {
            addNotification({ variant: "warn", primaryText: t("PartPro Trade not updated"), secondaryText: res.message });
          }
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <LoadingWheel />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-red-600 font-medium">
        {t("Unable to load part categories.")}
      </div>
    );
  }

  const zeroPrice = PriceFormat(0);
  const autoSelectHint = t(
    "On Create Jobs this account is picked for the category when nobody prices it higher, so the part is always created and labelled. A real price in the pricing matrix still wins."
  );

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">{t("Part Categories")}</h3>
        <ButtonInput
          label={t("Save")}
          isSubmit={false}
          onClick={handleSave}
          classes=""
          isLoading={update.isLoading}
        />
      </div>

      <p className="text-sm text-gray-500">
        {t("Choose which part categories this account can use.")}
        {` ${t("Tick Auto-select on a category to make this account its {{price}} default: {{hint}}", { price: zeroPrice, hint: autoSelectHint })}`}
        {hasPartProTrade &&
          ` ${t("A linked PartPro Trade buyer can bid on these categories wherever you offer them on PartPro Trade. Categories you grant or revoke for them in PartPro Trade (including approved requests) show up here too.")}`}
      </p>

      <PartCategoryCombo
        handleAddCategory={handleAddCategory}
        placeholder={t("Search and add a part category")}
      />

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">
          {t("Selected Categories ({{count}})", { count: sortedSelectedCategories.length })}
        </p>

        {sortedSelectedCategories.length === 0 ? (
          <div className="text-sm text-gray-600 border border-gray-200 rounded-md bg-white p-3">
            {t("No categories selected.")}
          </div>
        ) : (
          <div className="space-y-2">
            {sortedSelectedCategories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2"
              >
                <span className="text-sm text-gray-800">{category.name}</span>
                <div className="flex items-center gap-3">
                  <Tooltip content={autoSelectHint}>
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={category.autoSelect}
                        onChange={(e) => handleToggleAutoSelect(category.id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      {t("Auto-select at {{price}}", { price: zeroPrice })}
                    </label>
                  </Tooltip>
                  <ButtonInput
                    label={t("Remove")}
                    isSubmit={false}
                    onClick={() => handleRemoveCategory(category.id)}
                    classes="!bg-red-500 hover:!bg-red-600"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
